import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { NewsFeed } from "../components/NewsFeed";
import { ReaderReports } from "../components/ReaderReports";
import { AdSlot } from "../components/AdSlot";
import { AD_SLOTS } from "../lib/adSlots";
import { RefreshCw, Search, ExternalLink, Newspaper, MessageSquare, X, AlertTriangle } from "lucide-react";

const statusText = {
  up: "Operational",
  slow: "Slow response",
  down: "Having issues",
};
const statusClass = {
  up: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  slow: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  down: "text-rose-300 bg-rose-500/10 border-rose-500/30",
};

export default function DownDetectorPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/services/status");
      setData(r.data.services || []);
      setLastChecked(r.data.checked_at);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const list = data.filter((s) => {
      if (filter !== "all" && s.status !== filter) return false;
      if (query && !s.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
    // Sort: down first, then slow, then by recent reports desc, then alpha
    const rank = { down: 0, slow: 1, up: 2 };
    return [...list].sort((a, b) => {
      const r = (rank[a.status] ?? 3) - (rank[b.status] ?? 3);
      if (r !== 0) return r;
      const rp = (b.reports_1h || 0) - (a.reports_1h || 0);
      if (rp !== 0) return rp;
      return a.name.localeCompare(b.name);
    });
  }, [data, filter, query]);

  const summary = useMemo(() => {
    if (!data) return { up: 0, slow: 0, down: 0 };
    return data.reduce((acc, s) => ({ ...acc, [s.status]: (acc[s.status] || 0) + 1 }), { up: 0, slow: 0, down: 0 });
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-10 lg:py-16">
      <header className="mb-8 sm:mb-10 flex flex-wrap items-end justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ Down Detector</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Are the big services alive?</h1>
          <p className="mt-3 text-sm sm:text-base text-[var(--text-dim)] max-w-2xl">
            We probe major services in real time and pull recent news &amp; community chatter when something looks off.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium" data-testid="dd-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-5 sm:mb-6">
        <SummaryCard label="Operational" count={summary.up} color="#34D399" testid="summary-up" />
        <SummaryCard label="Slow" count={summary.slow} color="#FBBF24" testid="summary-slow" />
        <SummaryCard label="Issues" count={summary.down} color="#F87171" testid="summary-down" />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 sm:gap-3 items-center mb-5 sm:mb-6">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            data-testid="dd-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a service…"
            className="w-full bg-[rgba(15,17,32,0.7)] border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--cobalt)] transition"
          />
        </div>
        <div className="flex gap-1 p-1 rounded-md bg-[rgba(15,17,32,0.7)] border border-white/10 overflow-x-auto">
          {[
            { k: "all", l: "All" },
            { k: "up", l: "Up" },
            { k: "slow", l: "Slow" },
            { k: "down", l: "Down" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`dd-filter-${t.k}`}
              className={`px-3 py-1 rounded text-xs font-medium transition ${
                filter === t.k ? "bg-[rgba(61,107,255,0.2)] text-white" : "text-[var(--text-dim)] hover:text-white"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        {lastChecked && (
          <span className="text-xs font-mono text-[var(--text-muted)] ml-auto">
            checked: {new Date(lastChecked).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Grid */}
      {loading && !data ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="glass rounded-xl p-4 h-24 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-[var(--text-dim)]" data-testid="dd-empty">No services match your filters.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dd-service-grid">
          {filtered.map((s) => (
            <button
              key={s.slug}
              onClick={() => setSelected(s)}
              className="glass rounded-xl p-4 lift text-left flex items-center gap-4 relative"
              data-testid={`dd-service-${s.slug}`}
            >
              <div className="w-11 h-11 rounded-lg flex items-center justify-center font-display text-lg font-semibold bg-[rgba(61,107,255,0.08)] border border-[rgba(61,107,255,0.2)] text-[var(--cobalt-bright)]">
                {s.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{s.name}</div>
                <div className="text-xs text-[var(--text-muted)] font-mono mt-0.5">
                  {s.category} · {s.latency_ms != null ? `${Math.round(s.latency_ms)} ms` : "—"}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className={`px-2 py-1 rounded-full border text-xs font-medium flex items-center gap-1.5 ${statusClass[s.status]}`}>
                  <span className={`dot dot-${s.status === "up" ? "up" : s.status === "slow" ? "slow" : "down"}`} />
                  {statusText[s.status]}
                </div>
                {s.reports_1h > 0 && (
                  <div className={`text-[10px] font-mono px-1.5 py-0.5 rounded flex items-center gap-1 ${s.reports_1h >= 5 ? "text-rose-300 bg-rose-500/10" : "text-amber-300 bg-amber-500/10"}`} data-testid={`dd-reports-${s.slug}`}>
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {s.reports_1h} report{s.reports_1h > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <DetailModal service={selected} onClose={() => setSelected(null)} />}

      {/* Ad: below status grid */}
      <div className="mt-8">
        <AdSlot slot={AD_SLOTS.downDetectorMid} name="down-detector-mid" minHeight={120} />
      </div>

      {/* Live feed */}
      <div className="mt-8 sm:mt-10">
        <NewsFeed title="Outage pulse · news, chatter & tips" testid="dd-news-feed" />
      </div>
    </div>
  );
}

function SummaryCard({ label, count, color, testid }) {
  return (
    <div className="glass rounded-xl p-3 sm:p-5 flex items-center gap-3 sm:gap-4 min-w-0" data-testid={testid}>
      <div className="w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}40` }}>
        <span className="dot" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] truncate">{label}</div>
        <div className="font-display text-xl sm:text-2xl font-semibold tabular-nums">{count}</div>
      </div>
    </div>
  );
}

function DetailModal({ service, onClose }) {
  const [news, setNews] = useState(null);
  const [chatter, setChatter] = useState(null);

  useEffect(() => {
    api.get("/services/news", { params: { service: service.name } }).then((r) => setNews(r.data.items || [])).catch(() => setNews([]));
    api.get("/services/chatter", { params: { service: service.name } }).then((r) => setChatter(r.data.items || [])).catch(() => setChatter([]));
  }, [service.name]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" data-testid="dd-detail-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-strong rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-display font-semibold bg-[rgba(61,107,255,0.12)] border border-[rgba(61,107,255,0.22)] text-[var(--cobalt-bright)]">
            {service.name.charAt(0)}
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-semibold">{service.name}</div>
            <div className="text-xs text-[var(--text-muted)] font-mono flex items-center gap-2 mt-0.5">
              <span className={`dot dot-${service.status === "up" ? "up" : service.status === "slow" ? "slow" : "down"}`} />
              {statusText[service.status]} · {service.latency_ms != null ? `${Math.round(service.latency_ms)} ms` : "—"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10" data-testid="dd-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto space-y-6">
          <ReaderReports slug={service.slug} serviceName={service.name} initial1h={service.reports_1h || 0} initial24h={service.reports_24h || 0} testid="dd-modal-reports" />

          <section>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] flex items-center gap-2 mb-3">
              <Newspaper className="w-3.5 h-3.5" /> Recent news
            </h4>
            {news === null ? (
              <div className="text-sm text-[var(--text-muted)]">Loading…</div>
            ) : news.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)]">No recent mentions found.</div>
            ) : (
              <ul className="space-y-3">
                {news.slice(0, 6).map((item, idx) => (
                  <li key={idx}>
                    <a href={item.link} target="_blank" rel="noreferrer" className="text-sm hover:text-[var(--cobalt-bright)] transition inline-flex items-start gap-2" data-testid={`dd-news-${idx}`}>
                      <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 opacity-60" />
                      <span>
                        <span className="block">{item.title}</span>
                        {item.source && <span className="text-xs text-[var(--text-muted)] font-mono mt-0.5 block">{item.source}</span>}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] flex items-center gap-2 mb-3">
              <MessageSquare className="w-3.5 h-3.5" /> Community chatter
            </h4>
            {chatter === null ? (
              <div className="text-sm text-[var(--text-muted)]">Loading…</div>
            ) : chatter.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)]">Nothing notable on Reddit right now.</div>
            ) : (
              <ul className="space-y-3">
                {chatter.slice(0, 6).map((item, idx) => (
                  <li key={idx}>
                    <a href={item.link} target="_blank" rel="noreferrer" className="text-sm hover:text-[var(--cobalt-bright)] transition flex items-start gap-2" data-testid={`dd-chatter-${idx}`}>
                      <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 opacity-60" />
                      <span>
                        <span className="block">{item.title}</span>
                        <span className="text-xs text-[var(--text-muted)] font-mono mt-0.5 block">{item.subreddit} · {item.num_comments} comments</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
